import {
  fitmentLevelLabel,
  getFitmentAdvice,
  renderFitmentAdvisor,
  shouldShowFitmentAdvisor,
} from '@theme/fitment-advisor';
import {
  getFitmentEvaluation,
  getFitmentResult,
  readStoredVehicle,
} from '@theme/fitment-matcher';
import { onDocumentReady } from '@theme/utilities';

/** @typedef {import('@theme/fitment-matcher').FitmentLevel} FitmentLevel */

const STATUS_COPY = {
  direct_fit: 'Direct fit — no modifications expected',
  modification_required: 'Fits with modifications — see details below',
  no_fit: 'Does not fit your selected vehicle',
};

/**
 * @param {Record<string, unknown> | null} vehicle
 */
function vehicleLabel(vehicle) {
  if (!vehicle) return '';
  return [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ');
}

/**
 * @param {FitmentLevel} result
 * @param {Record<string, unknown> | null} vehicle
 * @param {string} optionText
 */
function getInlineStatusText(result, vehicle, optionText) {
  if (!vehicle) {
    return 'Select your vehicle to validate compatibility.';
  }

  const label = vehicleLabel(vehicle);

  if (result === 'direct_fit') {
    return `Direct fit for your vehicle (${label}).`;
  }

  if (result === 'modification_required') {
    const evaluation = optionText ? getFitmentEvaluation(optionText, vehicle) : null;
    if (evaluation?.needsWheelAdapter) {
      return `Requires wheel adapters for ${label} — review fitment details.`;
    }
    return `Fits with modifications for ${label} — review fitment details.`;
  }

  if (result === 'no_fit') {
    return `Does not fit your selected vehicle (${label}).`;
  }

  if (!optionText) {
    return `Verified for ${label}. Select a specification to check fitment.`;
  }

  return `Verified for ${label}. Fitment could not be fully verified for this specification.`;
}

function optionLabelFromElement(option) {
  if (!(option instanceof HTMLOptionElement)) return '';
  const value = option.value.trim();
  if (value) return value;
  return option.textContent?.replace(/\s*-\s*Unavailable\s*$/i, '').trim() ?? '';
}

function selectedOptionText(variantPicker) {
  /** @param {string} text */
  const isSpecText = (text) => text.includes('|') || text.includes('丨');

  const selects = variantPicker.querySelectorAll('select.variant-option__select');
  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement) || !select.selectedOptions.length) continue;
    const text = optionLabelFromElement(select.selectedOptions[0]);
    if (isSpecText(text)) return text;
  }

  const checkedInputs = variantPicker.querySelectorAll('fieldset input:checked');
  for (const checked of checkedInputs) {
    if (!(checked instanceof HTMLInputElement)) continue;
    const label = checked.closest('label');
    const text = label?.textContent?.trim() ?? checked.value.trim();
    if (isSpecText(text)) return text;
  }

  const select = variantPicker.querySelector('select.variant-option__select');
  if (select instanceof HTMLSelectElement && select.selectedOptions.length) {
    return optionLabelFromElement(select.selectedOptions[0]);
  }

  const checked = variantPicker.querySelector('fieldset input:checked');
  if (checked instanceof HTMLInputElement) {
    const label = checked.closest('label');
    return label?.textContent?.trim() ?? checked.value.trim();
  }

  return '';
}

function ensureStatusElement(variantPicker) {
  const container = variantPicker.parentElement ?? variantPicker;
  const existing = container.querySelectorAll('[data-fitment-product-status]');
  let status = existing[0];

  existing.forEach((el, index) => {
    if (index > 0) el.remove();
  });

  if (status instanceof HTMLElement) return status;

  status = document.createElement('p');
  status.className = 'method-fitment-product-status hidden';
  status.dataset.fitmentProductStatus = '';
  variantPicker.insertAdjacentElement('afterend', status);
  return status;
}

function ensureFitmentAdvisorContainers() {
  document.querySelectorAll('[data-fitment-status-line]').forEach((statusLine) => {
    if (!(statusLine instanceof HTMLElement)) return;

    const parent = statusLine.parentElement;
    const existingAdvisor = parent?.querySelector('[data-fitment-advisor]');
    if (existingAdvisor instanceof HTMLElement) return;

    const advisor = document.createElement('div');
    advisor.className = 'fitment-advisor hidden';
    advisor.dataset.fitmentAdvisor = '';
    advisor.setAttribute('aria-live', 'polite');
    advisor.innerHTML = `
      <h3 class="fitment-advisor__heading">Fitment Advisor</h3>
      <div data-fitment-advisor-content></div>
    `;
    statusLine.insertAdjacentElement('afterend', advisor);
  });
}

/**
 * @param {FitmentLevel} result
 */
function statusLineClass(result) {
  switch (result) {
    case 'direct_fit':
      return 'method-fitment-status-line--direct-fit';
    case 'modification_required':
      return 'method-fitment-status-line--modification';
    case 'no_fit':
      return 'method-fitment-status-line--no-fit';
    default:
      return 'method-fitment-status-line--verified';
  }
}

/**
 * @param {FitmentLevel} result
 */
function productStatusClass(result) {
  switch (result) {
    case 'direct_fit':
      return 'method-fitment-product-status--direct-fit';
    case 'modification_required':
      return 'method-fitment-product-status--modification';
    case 'no_fit':
      return 'method-fitment-product-status--no-fit';
    default:
      return 'method-fitment-product-status--unknown';
  }
}

function updateInlineStatusElements(result, vehicle, optionText) {
  const text = getInlineStatusText(result, vehicle, optionText);

  document.querySelectorAll('[data-fitment-status-value]').forEach((el) => {
    el.textContent = text;
  });

  document.querySelectorAll('[data-fitment-status-line]').forEach((line) => {
    if (!(line instanceof HTMLElement)) return;
    line.classList.remove(
      'method-fitment-status-line--direct-fit',
      'method-fitment-status-line--modification',
      'method-fitment-status-line--no-fit',
      'method-fitment-status-line--verified',
      'method-fitment-status-line--fits'
    );

    if (!vehicle) return;

    line.classList.add(statusLineClass(result));
  });
}

function updateFitmentAdvisor(result, vehicle, optionText) {
  ensureFitmentAdvisorContainers();

  document.querySelectorAll('[data-fitment-advisor]').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    if (!shouldShowFitmentAdvisor(optionText, vehicle, result)) {
      container.classList.add('hidden');
      return;
    }

    renderFitmentAdvisor(container, getFitmentAdvice(optionText, vehicle));
  });
}

function updateProductFitment() {
  const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const vehicle = readStoredVehicle();
  const optionText =
    variantPicker instanceof HTMLElement ? selectedOptionText(variantPicker) : '';
  const result = optionText ? getFitmentResult(optionText, vehicle) : vehicle ? 'unknown' : 'no_vehicle';

  updateInlineStatusElements(result, vehicle, optionText);
  updateFitmentAdvisor(result, vehicle, optionText);

  if (!(variantPicker instanceof HTMLElement)) return;

  const status = ensureStatusElement(variantPicker);

  status.classList.remove(
    'method-fitment-product-status--direct-fit',
    'method-fitment-product-status--modification',
    'method-fitment-product-status--no-fit',
    'method-fitment-product-status--unknown',
    'method-fitment-product-status--fits'
  );

  if (result === 'no_vehicle' || result === 'unknown' || !STATUS_COPY[result]) {
    status.classList.add('hidden');
    status.textContent = '';
    return;
  }

  status.textContent =
    (() => {
      if (result !== 'modification_required' || !optionText) {
        return STATUS_COPY[result] ?? fitmentLevelLabel(result);
      }
      const evaluation = getFitmentEvaluation(optionText, vehicle);
      if (evaluation?.needsWheelAdapter) {
        return 'Requires wheel adapters — see details below';
      }
      return STATUS_COPY[result] ?? fitmentLevelLabel(result);
    })();
  status.classList.remove('hidden');
  status.classList.add(productStatusClass(result));
}

/** @type {(() => void) | null} */
let pendingProceed = null;

/** @type {boolean} */
let fitmentGateBypass = false;

/** @type {string | null} */
let acknowledgedKey = null;

let purchaseGateRegistered = false;

function isProductDetailWithFitment() {
  return Boolean(
    document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('[data-fitment-status-value]')
  );
}

function getProductFitmentContext() {
  const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const vehicle = readStoredVehicle();
  const optionText =
    variantPicker instanceof HTMLElement ? selectedOptionText(variantPicker) : '';
  const result = optionText ? getFitmentResult(optionText, vehicle) : vehicle ? 'unknown' : 'no_vehicle';

  return { variantPicker, vehicle, optionText, result };
}

function getAcknowledgmentKey() {
  const ctx = getProductFitmentContext();
  const variantId =
    document.querySelector('product-form-component input[name="id"]')?.value?.toString() ?? '';
  const vehicleKey = ctx.vehicle
    ? [ctx.vehicle.year, ctx.vehicle.make, ctx.vehicle.model, ctx.vehicle.trim].join('|')
    : '';
  return `${variantId}::${vehicleKey}::${ctx.optionText}::${ctx.result}`;
}

function getPrimaryFitmentAdvisor() {
  return document.querySelector('[data-fitment-advisor][data-fitment-level]:not([data-fitment-level=""])');
}

function ensureFitmentAdvisorDialog() {
  let dialog = document.getElementById('fitment-advisor-dialog');
  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement('dialog');
  dialog.id = 'fitment-advisor-dialog';
  dialog.className = 'dialog-modal fitment-advisor-dialog color-scheme-1';
  dialog.setAttribute('aria-labelledby', 'fitment-advisor-dialog-title');
  dialog.innerHTML = `
    <div class="fitment-advisor-dialog__header">
      <h2 id="fitment-advisor-dialog-title" class="fitment-advisor-dialog__title">Fitment Advisor</h2>
      <button type="button" class="button button-unstyled fitment-advisor-dialog__close" data-fitment-advisor-close aria-label="Close">
        <span aria-hidden="true">×</span>
      </button>
    </div>
    <div class="fitment-advisor-dialog__body" data-fitment-advisor-dialog-body></div>
    <div class="fitment-advisor-dialog__actions">
      <button type="button" class="button" data-fitment-advisor-continue>I understand / Continue purchase</button>
      <button type="button" class="button button-secondary" data-fitment-advisor-cancel>Cancel</button>
    </div>
  `;

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    cancelFitmentAcknowledgment();
  });

  dialog.querySelector('[data-fitment-advisor-close]')?.addEventListener('click', cancelFitmentAcknowledgment);
  dialog.querySelector('[data-fitment-advisor-cancel]')?.addEventListener('click', cancelFitmentAcknowledgment);
  dialog.querySelector('[data-fitment-advisor-continue]')?.addEventListener('click', confirmFitmentAcknowledgment);

  document.body.appendChild(dialog);
  return dialog;
}

function openFitmentAdvisorDialog() {
  const source = getPrimaryFitmentAdvisor();
  const dialog = ensureFitmentAdvisorDialog();
  const body = dialog.querySelector('[data-fitment-advisor-dialog-body]');

  if (body instanceof HTMLElement) {
    body.replaceChildren();
    if (source instanceof HTMLElement) {
      const clone = source.cloneNode(true);
      if (clone instanceof HTMLElement) {
        clone.classList.remove('hidden');
        body.append(clone);
      }
    }
  }

  document.body.classList.add('fitment-advisor-dialog-open');
  dialog.showModal();
}

function closeFitmentAdvisorDialog() {
  const dialog = document.getElementById('fitment-advisor-dialog');
  if (dialog instanceof HTMLDialogElement && dialog.open) {
    dialog.close();
  }
  document.body.classList.remove('fitment-advisor-dialog-open');
}

export function shouldRequireFitmentAcknowledgment() {
  if (fitmentGateBypass || !isProductDetailWithFitment()) return false;

  const ctx = getProductFitmentContext();
  if (!shouldShowFitmentAdvisor(ctx.optionText, ctx.vehicle, ctx.result)) return false;
  if (!getFitmentAdvice(ctx.optionText, ctx.vehicle)) return false;
  if (acknowledgedKey === getAcknowledgmentKey()) return false;

  return true;
}

export function requestFitmentAcknowledgment(proceed) {
  if (!shouldRequireFitmentAcknowledgment()) {
    proceed();
    return;
  }

  pendingProceed = proceed;
  openFitmentAdvisorDialog();
}

export function confirmFitmentAcknowledgment() {
  acknowledgedKey = getAcknowledgmentKey();
  closeFitmentAdvisorDialog();

  const proceed = pendingProceed;
  pendingProceed = null;
  if (!proceed) return;

  fitmentGateBypass = true;
  try {
    proceed();
  } finally {
    fitmentGateBypass = false;
  }
}

export function cancelFitmentAcknowledgment() {
  pendingProceed = null;
  closeFitmentAdvisorDialog();
}

/**
 * Registers capture-phase Buy it now interception (must load via product-form import).
 */
export function registerFitmentPurchaseGate() {
  if (purchaseGateRegistered) return;
  purchaseGateRegistered = true;

  document.addEventListener(
    'click',
    (event) => {
      if (fitmentGateBypass) return;

      const path = event.composedPath?.() ?? [];
      const button = path.find(
        (node) =>
          node instanceof HTMLButtonElement && node.classList.contains('shopify-payment-button__button')
      );

      if (!(button instanceof HTMLButtonElement)) return;
      if (!button.closest('product-form-component')) return;
      if (!isProductDetailWithFitment()) return;
      if (!shouldRequireFitmentAcknowledgment()) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      pendingProceed = () => {
        fitmentGateBypass = true;
        try {
          button.click();
        } finally {
          fitmentGateBypass = false;
        }
      };
      openFitmentAdvisorDialog();
    },
    true
  );
}

let fitmentInitialized = false;

function initProductFitment() {
  if (fitmentInitialized) {
    updateProductFitment();
    return;
  }

  if (
    !document.querySelector('variant-picker[data-template-product-match="true"]') &&
    !document.querySelector('[data-fitment-status-value]')
  ) {
    return;
  }

  fitmentInitialized = true;
  updateProductFitment();
  document.addEventListener('vehiclefitment:change', updateProductFitment);
  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('variant-picker[data-template-product-match="true"]')) {
      updateProductFitment();
    }
  });
}

onDocumentReady(initProductFitment);
